/**
 * Level 94: the houses, and the hills they stand on.
 *
 * Split out of `propsuburb.ts` because a house is not a garden ornament. The
 * rest of the level's scenery is one object standing on one cell; these three
 * kinds are *fitted to a building* — they are sized in grid cells, they are
 * placed by `world/gen/motion.ts` against a rectangle of wall it carved
 * earlier, and every measurement in here is derived from the same cell size the
 * generator used.
 *
 * ## Why a house needs scenery at all
 *
 * A house on this level is a ring of `Cell.Wall` and the mesh builder draws it,
 * so its shape is not something this file can change. What the mesh builder
 * draws is a flat-topped box: wall from the ground to `spec.ceilingHeight`, and
 * then — because every lane on Level 94 is `Cell.Open` and an open cell with a
 * solid neighbour needs something above the ceiling line or the player looks
 * straight out over the roofs — a *collar* on top of it. So a house presents
 * `ceilingHeight + min(4, ceilingHeight * 1.3)` metres of blank wallpaper to the
 * street and stops dead. That is a warehouse, not a house.
 *
 * Three kinds put the house back:
 *
 *  - **`house-hill`** — the rounded green mound each house stands on, which is
 *    the thing the level is actually known for. It is a *ring*: there is no
 *    geometry inside the house's own rectangle, because a dome under the floor
 *    would be a green hill standing in somebody's front room, seen through the
 *    open front door. The ring's inner edge meets the wall at crest height and
 *    falls to nothing at the rim, so the wall grows out of the bank with no gap.
 *  - **`house-roof`** — eaves, pitch, verge, chimney and the string course that
 *    turns four unbroken metres of wallpaper into two storeys.
 *  - **`house-trim`** — one bay of windows, top and bottom, or the hood over a
 *    front door. Placed against a wall cell by the generator, which is the only
 *    thing that knows where the door actually is.
 *
 * ## The two contracts
 *
 * 1. **Size is a variant, not a scale.** `PropSpot.scale` is uniform, and a
 *    roof for a five-by-four house is not a scaled roof for a four-by-four one.
 *    So every size is baked, over cell counts `SIZE_MIN`..`SIZE_MAX` on both
 *    axes, and the generator picks the index. `world/gen/motion.ts` repeats the
 *    encoding below rather than importing it — `world` never imports `render` —
 *    so the two have to be changed together. They are ten lines apart in the
 *    only two files that mention a house.
 * 2. **Origin.** `house-hill` is authored about the middle of the house and
 *    turned by `spot.yaw` so its graded front faces the lane. `house-roof` is
 *    authored about the house's *minimum corner cell* and is never turned: it
 *    needs a footprint tall enough to be exempt from the scenery cull (see
 *    `LANDMARK_HEIGHT` in `renderer.ts`, a roof that pops in at sixty-eight
 *    metres is worse than no roof), and the only cell of a house that can carry
 *    a collision box without putting an invisible post in the living room is one
 *    that is already wall.
 */

import * as THREE from 'three';
import type { LevelSpec } from '../core/types';
import { box, cyl, makeShape, matte, merge, type PropLayer, type PropShape } from './propkit';
import { suburb } from './propsuburb';

/* ------------------------------------------------------------------ *
 * The variant encoding, shared with world/gen/motion.ts
 * ------------------------------------------------------------------ */

/** Smallest building this file bakes geometry for, in cells. A garage. */
export const SIZE_MIN = 2;
/** Largest. A plot is six or seven cells and a house is set inside one. */
export const SIZE_MAX = 6;
/** Cell counts covered on each axis. */
const SIZES = SIZE_MAX - SIZE_MIN + 1;
/** Ridge along X, ridge along Z, hipped. */
const STYLES = 3;
/** Tile, slate, and the green one. */
const COLOURS = 3;

/** Clamp a building's cell count into the range geometry exists for. */
function sizeAxis(cells: number): number {
  return Math.max(SIZE_MIN, Math.min(SIZE_MAX, Math.round(cells)));
}

/** Index of one (width, depth) pair in cells. */
function sizeIndex(w: number, d: number): number {
  return (sizeAxis(w) - SIZE_MIN) * SIZES + (sizeAxis(d) - SIZE_MIN);
}

/** Variant number for a `house-hill` on a `w` by `d` cell building. */
export function hillVariant(w: number, d: number): number {
  return sizeIndex(w, d);
}

/**
 * Variant number for a `house-roof`. `style` and `colour` are free choices the
 * generator makes per house; everything else follows from the footprint.
 */
export function roofVariant(w: number, d: number, style: number, colour: number): number {
  const t = ((style % STYLES) + STYLES) % STYLES;
  const c = ((colour % COLOURS) + COLOURS) % COLOURS;
  return (c * STYLES + t) * SIZES * SIZES + sizeIndex(w, d);
}

/* ------------------------------------------------------------------ *
 * Measurements
 * ------------------------------------------------------------------ */

/**
 * Top of the blank wall a house shows the street.
 *
 * This mirrors the collar in `render/meshbuilder.ts` — `shaftY = min(4,
 * ceilingHeight * 1.3)`, raised above `ceilingHeight` on every `Cell.Open`
 * whose neighbour is not open. Nothing ties the two numbers together, so if the
 * collar ever changes, this changes with it or every roof on the level floats.
 * It is worth the duplication: the alternative is a roof authored to a constant
 * that has nothing to do with the wall it sits on.
 */
export function eavesHeight(spec: LevelSpec): number {
  return spec.ceilingHeight + Math.min(4, spec.ceilingHeight * 1.3);
}

/** Metres the roof oversails the wall below it. */
const OVERHANG = 0.42;
/** Depth of the eaves board, and the thickness the roof reads as. */
const FASCIA = 0.26;
/**
 * Roof pitch as rise over half-span. Shallow on purpose: the water tower is
 * twelve metres and is the only landmark on the level, and a steep roof on a
 * nineteen-metre house puts a ridge within a metre of it.
 */
const PITCH = 0.28;
/** Highest a ridge may sit above its eaves, whatever the span. */
const RISE_MAX = 2.0;

/**
 * The mound, in two stages, and the numbers are a compromise the level cannot
 * get out of.
 *
 * The floor of this game is flat: the player walks at ground height whatever is
 * drawn under them, so a bank they can stand on is a bank they stand *in*. The
 * height of the mound where a player can reach it is therefore how deep in the
 * grass they appear to be, and they can reach to within a body radius of every
 * outside wall.
 *
 * So the bank is short and steep against the wall — 56 cm, which at the closest
 * a player can get is about knee height and reads as scrambling up a bank — and
 * then a second, much wider and much lower swell carries the shape out another
 * two and a half metres. The swell is what does the work from the street: it is
 * never more than 17 cm, so nothing walking over it sinks, and it turns a plinth
 * into a hill you can see from the far side of the lane.
 */
const SKIRT_IN = 1.7;
const SKIRT_OUT = 2.6;
/** Bank height where it meets the wall, on the three sides nobody enters by. */
const CREST = 0.56;
/** Height at the join between the two stages. Both are flat here, so it is smooth. */
const CREST_MID = 0.17;
/**
 * Bank height at the bottom of the cutting through the front.
 *
 * The path to the door has to arrive at the threshold and the level's own
 * doorways sit on the ground, so half a metre of bank across the front would
 * take the bottom out of the front door. Only about a third of the face is cut
 * away — see the grading in `mound` — so this is the floor of a drive rather
 * than the height of the whole front lawn.
 */
const CREST_FRONT = 0.13;
/** Metres the bank stands proud of the wall face, so no seam can open up. */
const LIP = 0.07;

/* ------------------------------------------------------------------ *
 * Local geometry helpers
 * ------------------------------------------------------------------ */

/** Smoothstep, clamped. */
function smooth01(t: number): number {
  const x = t <= 0 ? 0 : t >= 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}

function layer(material: THREE.Material, variants: Array<THREE.BufferGeometry | null>): PropLayer {
  return { material, variants };
}

/** A triangle, wound so `a -> b -> c` faces the way you want it seen. */
function tri(
  out: number[],
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  cx: number,
  cy: number,
  cz: number
): void {
  out.push(ax, ay, az, bx, by, bz, cx, cy, cz);
}

/**
 * A quad as two triangles, wound the same way.
 *
 * Collapses to one triangle when the far edge has no length, which is what a
 * hipped roof on a square building is: the ridge is a point and both slopes
 * meet at it. A zero-area triangle would otherwise reach `computeVertexNormals`
 * and come back with a normal of NaN.
 */
function quad(
  out: number[],
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  cx: number,
  cy: number,
  cz: number,
  dx: number,
  dy: number,
  dz: number
): void {
  tri(out, ax, ay, az, bx, by, bz, cx, cy, cz);
  const flat = Math.abs(cx - dx) + Math.abs(cy - dy) + Math.abs(cz - dz);
  if (flat > 1e-4) tri(out, ax, ay, az, cx, cy, cz, dx, dy, dz);
}

/**
 * Turn a raw triangle soup into a buffer with normals.
 *
 * Everything else in the prop kit is built from three's primitives, which come
 * with normals; the roof planes and the mound are not any primitive, so they
 * are written out as positions and given flat normals here. Flat is right for
 * both: a stop-motion roof has creases, not shading gradients.
 */
function soup(positions: readonly number[]): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  g.computeVertexNormals();
  return g;
}

/* ================================================================== *
 * The hill
 * ================================================================== */

/** Sectors around one mound. Thirty-two keeps the chord error inside the wall lip. */
const MOUND_SECTORS = 32;
/** Rings across the steep inner bank, and across the wide outer swell. */
const MOUND_RINGS_IN = 2;
const MOUND_RINGS_OUT = 3;

/**
 * One rounded bank around one building.
 *
 * Built as a ring rather than a dome, and the ring is the whole trick: the
 * inner edge sits on the wall face at crest height and there is no geometry at
 * all inside the building, so a player standing in the front room sees a floor
 * rather than a hillside. The profile is `cos²`, which is flat at both ends —
 * flat at the wall so the bank has a shoulder to stand the house on, flat at
 * the rim so it disappears into the lawn instead of ending in a cliff.
 *
 * `ax` and `az` are the building's half extents in metres; the front of the
 * building faces local -Z.
 */
function mound(ax: number, az: number): THREE.BufferGeometry {
  const N = MOUND_SECTORS;
  const inX = ax + LIP;
  const inZ = az + LIP;

  const baseR = new Float64Array(N);
  const crest = new Float64Array(N);
  const mid = new Float64Array(N);
  for (let i = 0; i < N; i++) {
    const phi = (i / N) * Math.PI * 2;
    const dx = Math.cos(phi);
    const dz = Math.sin(phi);
    // Where this direction leaves the building's rectangle.
    baseR[i] = 1 / Math.max(Math.abs(dx) / inX, Math.abs(dz) / inZ);
    // Graded down where the path to the door arrives and back up well before
    // the corners. Narrow on purpose: the front is the elevation a player
    // actually walks past, and a mound cut away across its whole width is a
    // house standing on nothing from the only angle that matters. What is
    // wanted is a drive cut into a hill, so the cutting is about a third of the
    // face and the corners keep their full bank.
    const grade = smooth01((-dz - 0.78) / 0.22);
    crest[i] = CREST + (CREST_FRONT - CREST) * grade;
    mid[i] = CREST_MID * (1 - grade * 0.6);
  }

  const pos: number[] = [];
  const idx: number[] = [];

  /** One ring of vertices. Returns the index of its first vertex. */
  const ring = (radius: (i: number) => number, y: (i: number) => number): number => {
    const first = pos.length / 3;
    for (let i = 0; i < N; i++) {
      const phi = (i / N) * Math.PI * 2;
      const r = radius(i);
      pos.push(Math.cos(phi) * r, y(i), Math.sin(phi) * r);
    }
    return first;
  };
  /**
   * Skin between two rings. `a` is the upper or inner one: wound so the strip
   * faces up and outward, which is the only side of a bank anyone ever sees.
   */
  const skin = (a: number, b: number): void => {
    for (let i = 0; i < N; i++) {
      const j = (i + 1) % N;
      idx.push(a + i, a + j, b + j, a + i, b + j, b + i);
    }
  };

  // The apron: a short vertical face against the wall, so no sliver of white
  // can show under the bank from a steep angle.
  const apronTop = ring((i) => baseR[i]!, (i) => crest[i]!);
  const apronFoot = ring((i) => baseR[i]!, () => 0);
  skin(apronTop, apronFoot);

  // The steep inner bank: crest down to the join, flat at both ends so the
  // house has a shoulder to stand on and the two stages meet without a crease.
  let prev = apronTop;
  for (let j = 1; j <= MOUND_RINGS_IN; j++) {
    const u = j / MOUND_RINGS_IN;
    const fall = Math.cos((u * Math.PI) / 2) ** 2;
    const next = ring(
      (i) => baseR[i]! + SKIRT_IN * u,
      (i) => mid[i]! + (crest[i]! - mid[i]!) * fall
    );
    skin(prev, next);
    prev = next;
  }
  // The wide low swell that carries the shape out to the lawn.
  for (let j = 1; j <= MOUND_RINGS_OUT; j++) {
    const u = j / MOUND_RINGS_OUT;
    const fall = Math.cos((u * Math.PI) / 2) ** 2;
    const next = ring((i) => baseR[i]! + SKIRT_IN + SKIRT_OUT * u, (i) => mid[i]! * fall);
    skin(prev, next);
    prev = next;
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  // Through `merge` so it comes out in the same position-and-normal layout as
  // every other prop buffer, and so the index is flattened away.
  return merge([g]);
}

/**
 * The green mound a house stands on: one variant per building size in cells.
 *
 * Turned by `PropSpot.yaw` so the graded front faces the lane, which means a
 * building whose front is on an X face is drawn from the *transposed* size —
 * the generator swaps the two cell counts before asking for a variant.
 */
export function buildHouseHill(spec: LevelSpec): PropShape {
  const pal = suburb(spec);
  const cs = spec.cellSize;

  const geos: Array<THREE.BufferGeometry | null> = [];
  for (let w = SIZE_MIN; w <= SIZE_MAX; w++) {
    for (let d = SIZE_MIN; d <= SIZE_MAX; d++) {
      geos.push(mound((w * cs) / 2, (d * cs) / 2));
    }
  }

  // The one place on the level a prop must match the *floor* rather than sit
  // against the level's air: a bank in a different green from the lawn it grows
  // out of reads as a tarpaulin.
  return makeShape(spec, { kind: 'floor', unroofed: true }, [layer(matte(pal.turf), geos)]);
}

/* ================================================================== *
 * The roof
 * ================================================================== */

/** What one roof shape needs to know. Everything is in metres about the house centre. */
interface RoofPlan {
  /** Half extents of the wall below, before the overhang. */
  ax: number;
  az: number;
  /** Half extents of the roof itself. */
  rx: number;
  rz: number;
  /** Underside of the roof, and the top of the wall. */
  eaves: number;
  /** Metres from eaves to ridge. */
  rise: number;
  /** Half length of the ridge along its own axis. Zero on a pyramid hip. */
  ridgeHalf: number;
  /** True when the ridge runs along X. */
  alongX: boolean;
  hipped: boolean;
}

function roofPlan(w: number, d: number, cs: number, eaves: number, style: number): RoofPlan {
  const ax = (w * cs) / 2;
  const az = (d * cs) / 2;
  const rx = ax + OVERHANG;
  const rz = az + OVERHANG;
  const hipped = style === 2;
  // A hip is pitched off the shorter span; a gable is pitched across the axis
  // its ridge does not run along.
  const alongX = hipped ? rx >= rz : style === 0;
  const span = alongX ? rz : rx;
  const rise = Math.min(RISE_MAX, span * PITCH);
  const ridgeHalf = hipped ? Math.max(0, (alongX ? rx : rz) - span) : alongX ? rx : rz;
  return { ax, az, rx, rz, eaves, rise, ridgeHalf, alongX, hipped };
}

/** The sloping planes, in the tile colour. */
function roofSlopes(p: RoofPlan): THREE.BufferGeometry {
  const out: number[] = [];
  const y0 = p.eaves;
  const y1 = p.eaves + p.rise;
  const { rx, rz, ridgeHalf, alongX } = p;

  if (alongX) {
    const r0 = -ridgeHalf;
    const r1 = ridgeHalf;
    // Front and back pitches. Wound so the outward face is the one you see.
    quad(out, -rx, y0, rz, rx, y0, rz, r1, y1, 0, r0, y1, 0);
    quad(out, rx, y0, -rz, -rx, y0, -rz, r0, y1, 0, r1, y1, 0);
    if (p.hipped) {
      tri(out, rx, y0, rz, rx, y0, -rz, r1, y1, 0);
      tri(out, -rx, y0, -rz, -rx, y0, rz, r0, y1, 0);
    }
  } else {
    const r0 = -ridgeHalf;
    const r1 = ridgeHalf;
    quad(out, rx, y0, rz, rx, y0, -rz, 0, y1, r0, 0, y1, r1);
    quad(out, -rx, y0, -rz, -rx, y0, rz, 0, y1, r1, 0, y1, r0);
    if (p.hipped) {
      tri(out, -rx, y0, rz, rx, y0, rz, 0, y1, r1);
      tri(out, rx, y0, -rz, -rx, y0, -rz, 0, y1, r0);
    }
  }
  return soup(out);
}

/**
 * Everything painted rather than tiled: the gable ends, the soffit under the
 * overhang, the fascia round the eaves, and the string course that puts a floor
 * line halfway up the wall.
 *
 * The string course is doing more work than it looks like. Without it the
 * collar above the ceiling line is four unbroken metres of the same wallpaper
 * as the storey below, and a house with one seven-metre storey is a hangar.
 */
function roofTrim(p: RoofPlan, ceilingHeight: number): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const out: number[] = [];
  const y0 = p.eaves;
  const y1 = p.eaves + p.rise;
  const { rx, rz, ridgeHalf, alongX } = p;

  // Soffit: the roof seen from underneath, so standing against the wall and
  // looking up is a ceiling rather than a hole into the sky.
  quad(out, -rx, y0, -rz, rx, y0, -rz, rx, y0, rz, -rx, y0, rz);

  // The gable ends: the triangle of wall between the eaves and the ridge, wound
  // to face out of the building rather than into it.
  if (!p.hipped) {
    if (alongX) {
      tri(out, rx, y0, rz, rx, y0, -rz, ridgeHalf, y1, 0);
      tri(out, -rx, y0, -rz, -rx, y0, rz, -ridgeHalf, y1, 0);
    } else {
      tri(out, -rx, y0, rz, rx, y0, rz, 0, y1, ridgeHalf);
      tri(out, rx, y0, -rz, -rx, y0, -rz, 0, y1, -ridgeHalf);
    }
  }
  parts.push(soup(out));

  // Fascia all the way round, hiding the cut edge of the slopes.
  parts.push(
    box(rx * 2 + 0.04, FASCIA, 0.09, 0, y0 - FASCIA / 2, rz + 0.02),
    box(rx * 2 + 0.04, FASCIA, 0.09, 0, y0 - FASCIA / 2, -rz - 0.02),
    box(0.09, FASCIA, rz * 2 + 0.04, rx + 0.02, y0 - FASCIA / 2, 0),
    box(0.09, FASCIA, rz * 2 + 0.04, -rx - 0.02, y0 - FASCIA / 2, 0)
  );

  // The floor line. Proud of the wall by six centimetres, which is enough to
  // catch a shadow at this light level and not enough to walk into at seven
  // metres up.
  const sy = ceilingHeight + 0.16;
  const sx = p.ax + 0.06;
  const sz = p.az + 0.06;
  parts.push(
    box(sx * 2, 0.13, 0.06, 0, sy, sz),
    box(sx * 2, 0.13, 0.06, 0, sy, -sz),
    box(0.06, 0.13, sz * 2, sx, sy, 0),
    box(0.06, 0.13, sz * 2, -sx, sy, 0)
  );

  return merge(parts);
}

/**
 * A chimney, or nothing on a building too small to have a fire in it.
 *
 * Set on a slope rather than astride the ridge, because a stack that breaks the
 * ridge line is the one detail that makes a simple roof read as a drawn one.
 */
function roofChimney(p: RoofPlan, w: number, d: number): THREE.BufferGeometry | null {
  if (w < 4 || d < 4) return null;
  // Kept short. The water tower is the only landmark on this level and it is
  // twelve metres; a stack that reaches for it stops being a chimney.
  const top = p.eaves + p.rise + 0.38;
  // Toward one corner of the plan, and always the same corner for a given size:
  // there is exactly one of these per house and it is not worth a seed.
  const cx = p.alongX ? p.ax * 0.44 : p.ax * 0.3;
  const cz = p.alongX ? p.az * 0.3 : p.az * 0.44;
  const base = p.eaves - 1.1;
  return merge([
    box(0.88, top - base, 0.7, cx, (top + base) / 2, cz),
    box(1.02, 0.12, 0.84, cx, top + 0.06, cz),
    cyl(0.13, 0.13, 0.26, 6, cx - 0.19, top + 0.24, cz),
    cyl(0.13, 0.13, 0.26, 6, cx + 0.19, top + 0.24, cz),
  ]);
}

/**
 * The roof over one house: pitch, eaves, chimney and floor line.
 *
 * Authored about the house's minimum corner cell rather than its middle — see
 * the note at the top of the file — so every variant carries the offset to the
 * middle baked in and the prop is never turned.
 */
export function buildHouseRoof(spec: LevelSpec): PropShape {
  const pal = suburb(spec);
  const cs = spec.cellSize;
  const eaves = eavesHeight(spec);

  /** One geometry per (style, size); the colour layers share them. */
  const slopes: Array<THREE.BufferGeometry | null> = [];
  const trims: Array<THREE.BufferGeometry | null> = [];
  const stacks: Array<THREE.BufferGeometry | null> = [];
  for (let t = 0; t < STYLES; t++) {
    for (let w = SIZE_MIN; w <= SIZE_MAX; w++) {
      for (let d = SIZE_MIN; d <= SIZE_MAX; d++) {
        const p = roofPlan(w, d, cs, eaves, t);
        // From the corner cell's middle to the building's middle.
        const ox = ((w - 1) / 2) * cs;
        const oz = ((d - 1) / 2) * cs;
        const slope = roofSlopes(p);
        const trim = roofTrim(p, spec.ceilingHeight);
        const stack = roofChimney(p, w, d);
        slope.translate(ox, 0, oz);
        trim.translate(ox, 0, oz);
        stack?.translate(ox, 0, oz);
        slopes.push(slope);
        trims.push(trim);
        stacks.push(stack);
      }
    }
  }

  const perColour = STYLES * SIZES * SIZES;
  const tile: Array<Array<THREE.BufferGeometry | null>> = [[], [], []];
  const trimVariants: Array<THREE.BufferGeometry | null> = [];
  const stackVariants: Array<THREE.BufferGeometry | null> = [];
  for (let c = 0; c < COLOURS; c++) {
    for (let i = 0; i < perColour; i++) {
      for (let k = 0; k < COLOURS; k++) tile[k]!.push(k === c ? slopes[i]! : null);
      trimVariants.push(trims[i]!);
      stackVariants.push(stacks[i] ?? null);
    }
  }

  return makeShape(spec, { kind: 'floor', unroofed: true }, [
    layer(matte(pal.tileRed), tile[0]!),
    layer(matte(pal.tileSlate), tile[1]!),
    layer(matte(pal.tileMoss), tile[2]!),
    layer(matte(pal.paint), trimVariants),
    layer(matte(pal.brick), stackVariants),
  ]);
}

/* ================================================================== *
 * The wall bay
 * ================================================================== */

/** Middle of the ground-floor opening. Below a standing player's eye line. */
const SILL_LOW = 1.82;
/** Middle of the upper-floor opening, which lands on the collar. */
const SILL_HIGH = 5.05;

/** One window's worth of parts, split by material. */
interface Bay {
  paint: THREE.BufferGeometry[];
  glass: THREE.BufferGeometry[];
  red: THREE.BufferGeometry[];
}

/**
 * A window: four boards round a dark pane, with a sill under it.
 *
 * The pane is nearly a hole and nothing behind it is modelled, which is the
 * whole level in one detail — every house is furnished exactly as far as the
 * outside of its walls.
 */
function windowUnit(bay: Bay, w: number, h: number, y: number, x: number, shutters: boolean): void {
  const t = 0.07;
  const f = 0.1;
  bay.glass.push(box(w, h, 0.03, x, y, -0.03));
  bay.paint.push(
    box(w + f * 2, f, t, x, y + h / 2 + f / 2, -0.05),
    box(w + f * 2, f, t, x, y - h / 2 - f / 2, -0.05),
    box(f, h, t, x - w / 2 - f / 2, y, -0.05),
    box(f, h, t, x + w / 2 + f / 2, y, -0.05),
    // Sill, wider than the opening and thrown a little further out.
    box(w + 0.34, 0.08, 0.17, x, y - h / 2 - f - 0.04, -0.07)
  );
  if (shutters) {
    const sw = w * 0.34;
    for (const s of [-1, 1]) {
      bay.red.push(box(sw, h + 0.06, 0.05, x + s * (w / 2 + f + sw / 2), y, -0.09));
    }
  }
}

/**
 * A bay of a house wall: windows top and bottom, or the hood over a front door.
 *
 * Authored the way every wall-mounted prop in the game is — the mounting face
 * at the local origin, the body running toward local -Z — so the generator can
 * stand one on the lawn cell outside a wall, face it out, and have it land flat
 * against the wallpaper.
 *
 * Nothing here carries a footprint. Everything that projects does so above
 * 2.3 m, except the doorstep, which is a step.
 */
export function buildHouseTrim(spec: LevelSpec): PropShape {
  const pal = suburb(spec);

  const make = (fill: (bay: Bay) => void): Bay => {
    const bay: Bay = { paint: [], glass: [], red: [] };
    fill(bay);
    return bay;
  };

  // 0: one tall opening on each floor. The plain one, and the most common.
  const plain = make((b) => {
    windowUnit(b, 1.05, 1.4, SILL_LOW, 0, false);
    windowUnit(b, 0.95, 1.15, SILL_HIGH, 0, false);
  });

  // 1: a wide opening below, a pair above. Shuttered, so a street of these is
  // not one house repeated.
  const wide = make((b) => {
    windowUnit(b, 2.0, 1.36, SILL_LOW, 0, true);
    windowUnit(b, 0.72, 1.05, SILL_HIGH, -0.62, false);
    windowUnit(b, 0.72, 1.05, SILL_HIGH, 0.62, false);
  });

  // 2: an oriel over an ordinary opening. It projects, so it is put on the
  // upper floor where there is nothing to walk into.
  const oriel = make((b) => {
    windowUnit(b, 1.2, 1.3, SILL_LOW, 0, true);
    const p = 0.36;
    b.paint.push(
      box(1.62, 1.34, 0.06, 0, SILL_HIGH, -p),
      box(0.1, 1.34, p, -0.76, SILL_HIGH, -p / 2),
      box(0.1, 1.34, p, 0.76, SILL_HIGH, -p / 2),
      box(1.74, 0.11, p + 0.14, 0, SILL_HIGH + 0.72, -p / 2),
      box(1.74, 0.11, p + 0.14, 0, SILL_HIGH - 0.72, -p / 2)
    );
    b.glass.push(box(1.42, 1.1, 0.03, 0, SILL_HIGH, -p - 0.04));
  });

  // 3: the front door. The door itself is the level's own `Cell.Doorway`, so
  // what goes here is what a doorway cannot be: a hood over it, on brackets,
  // and a step up to the threshold.
  const porch = make((b) => {
    const hood = 2.42;
    const proj = 1.06;
    b.red.push(
      box(2.3, 0.14, proj + 0.12, 0, hood + 0.5, -proj / 2),
      box(2.16, 0.12, proj, 0, hood + 0.4, -proj / 2 - 0.02)
    );
    b.paint.push(
      box(2.34, 0.12, 0.12, 0, hood + 0.44, -proj - 0.02),
      box(0.13, 0.62, proj, -1.02, hood + 0.1, -proj / 2),
      box(0.13, 0.62, proj, 1.02, hood + 0.1, -proj / 2),
      // Doorstep. Two shallow risers, well under the height a player steps up.
      box(2.0, 0.11, 0.9, 0, 0.055, -0.45),
      box(2.34, 0.11, 0.52, 0, 0.165, -0.26)
    );
    windowUnit(b, 1.0, 1.05, SILL_HIGH, 0, false);
  });

  const bays = [plain, wide, oriel, porch];
  return makeShape(spec, { kind: 'floor', unroofed: true }, [
    layer(matte(pal.paint), bays.map((b) => merge(b.paint))),
    layer(matte(pal.glass, 0.02), bays.map((b) => merge(b.glass))),
    layer(matte(pal.red), bays.map((b) => (b.red.length ? merge(b.red) : null))),
  ]);
}
